import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database } from 'lucide-react';
import SEO from '../components/SEO';

const PrivacyPolicy: React.FC = () => {
  const sections = [
    {
      title: "1. Introduction",
      content: [
        "At Aedon Community, we value the security of your personal data.",
        "This privacy policy explains how data collected through our website and Discord bot is used.",
        "By accepting this policy, you consent to your data being processed in this manner.",
        "Our policy complies with applicable data protection laws and regulations."
      ]
    },
    {
      title: "2. Data We Collect",
      content: [
        "**Discord Bot Data:** User IDs, server IDs, message logs, moderation records",
        "**Website Data:** IP addresses, browser information, page visits, cookies",
        "**Server Data:** Player statistics, server performance data, error logs",
        "**Contact Data:** Email addresses (only when you contact us)",
        "**Sponsor Interaction Data:** Sponsor link click data, advertisement interactions"
      ]
    },
    {
      title: "3. Data Usage Purposes",
      content: [
        "To improve and develop our services",
        "For security and moderation purposes",
        "To solve technical issues and optimize performance",
        "To fulfill legal obligations",
        "To measure the effectiveness of sponsored content",
        "To personalize user experience"
      ]
    },
    {
      title: "4. Discord Bot Data Processing",
      content: [
        "The bot accesses server data through Discord API",
        "Message contents are temporarily stored for moderation commands",
        "User preferences and settings are stored in database",
        "Bot logs are kept for security and debugging purposes",
        "Discord's own privacy policy also applies"
      ]
    },
    {
      title: "5. Website Data Processing",
      content: [
        "Visitor statistics are collected with Google Analytics",
        "Cookies are used to improve user experience",
        "Form data is stored only for communication purposes",
        "Server logs are kept for security",
        "Sponsor link clicks are analyzed"
      ]
    },
    {
      title: "6. Sponsored Content and Advertisements",
      content: [
        "Click data from sponsor links such as MrGamb is collected",
        "This data is used for sponsor interaction analysis",
        "User data redirected to sponsor sites is not under our control",
        "Sponsor sites' own privacy policies apply",
        "Age verification is performed for users redirected to betting sites",
        "Users from regions where gambling is illegal are responsible for their own actions",
        "We do not accept responsibility for users from gambling-prohibited regions"
      ]
    },
    {
      title: "7. Data Sharing",
      content: [
        "We do not share your data with third parties (except legal requirements)",
        "Only anonymous statistics are shared with sponsor companies",
        "Bot data is shared with Discord (Discord API requirement)",
        "Data sharing may occur upon legal authorities' requests",
        "Data sharing with our service providers is minimal"
      ]
    },
    {
      title: "8. Data Security",
      content: [
        "Your data is protected with SSL encryption",
        "Our servers are located in secure data centers",
        "Regular security updates are performed",
        "Data access is limited to authorized personnel only",
        "In case of data breach, notification is made within 72 hours"
      ]
    },
    {
      title: "9. Data Retention Periods",
      content: [
        "Discord bot data: Until account deletion",
        "Website logs: 1 year",
        "Analytics data: 2 years",
        "Contact data: 3 years",
        "Sponsor interaction data: 1 year",
        "Security logs: 5 years"
      ]
    },
    {
      title: "10. User Rights",
      content: [
        "You can request access to your data",
        "You can request correction of your data",
        "You can request deletion of your data",
        "You can object to data processing",
        "You can request a portable copy of your data",
        "You can request processing restrictions"
      ]
    },
    {
      title: "11. Cookies",
      content: [
        "**Necessary Cookies:** Required for site functions",
        "**Analytics Cookies:** For visitor statistics",
        "**Functional Cookies:** For user preferences",
        "**Advertising Cookies:** For sponsored content",
        "You can control cookies through browser settings"
      ]
    },
    {
      title: "12. Third-Party Services",
      content: [
        "**Google Analytics:** Visitor analysis",
        "**Cloudflare:** CDN and security",
        "**Discord API:** Bot functions",
        "**Sponsor Platforms:** Advertising and affiliate",
        "These services' own privacy policies apply"
      ]
    },
    {
      title: "13. Children's Privacy",
      content: [
        "We do not knowingly collect data from children under 13",
        "We do not redirect users under 18 to betting sites",
        "We do not process children's data without parental consent",
        "Children's data is immediately deleted if detected",
        "Parents can request deletion of children's data"
      ]
    },
    {
      title: "14. Policy Changes",
      content: [
        "We may change this policy without prior notice",
        "Changes will be published on our website",
        "Important changes will be notified via email",
        "Service usage after changes means acceptance of new policy",
        "Old policies are archived"
      ]
    },
    {
      title: "15. Contact",
      content: [
        "For privacy questions: privacy@aedoncommunity.com",
        "For data access requests: data@aedoncommunity.com",
        "General contact: info@aedoncommunity.com",
        "Discord: https://discord.gg/aedonv",
        "Website: https://aedoncommunity.com"
      ]
    }
  ];

  return (
    <>
      <SEO 
        title="Privacy Policy - Aedon Community"
        description="Privacy policy for Aedon Community and Discord bot. Detailed information about how personal data is collected, used and protected."
        keywords="Aedon Community privacy policy, Discord bot data protection, personal data, cookie policy"
        url="/privacy-policy"
      />
      <div className="min-h-screen pt-20 bg-dark-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center mb-6">
              <Lock className="w-12 h-12 text-primary-400 mr-4" />
              <h1 className="text-4xl md:text-5xl font-gaming font-bold text-white">
                Privacy Policy
              </h1>
            </div>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Information about how your personal data is protected
            </p>
            <div className="mt-6 flex items-center justify-center space-x-4 text-sm text-gray-500">
              <span>Last updated: December 22, 2024</span>
              <span>•</span>
              <span>Version: 1.0</span>
            </div>
          </motion.div>

          {/* Privacy Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-6 mb-8"
          >
            <div className="flex items-start space-x-3">
              <Shield className="w-6 h-6 text-blue-400 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-blue-400 mb-2">
                  Our Data Protection Commitment
                </h3>
                <p className="text-gray-300">
                  The security of your personal data is important to us. This policy explains how your data is collected, 
                  used and protected. Please read the sections about Discord bot data and sponsor interactions carefully.
                  Users from regions where gambling is illegal are responsible for their own actions.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Policy Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                className="card"
              >
                <h2 className="text-2xl font-gaming font-bold text-white mb-6 flex items-center">
                  <Database className="w-6 h-6 text-primary-400 mr-3" />
                  {section.title}
                </h2>
                <div className="space-y-4">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-start space-x-3">
                      <Eye className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                      <p className="text-gray-300 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-12 text-center"
          >
            <div className="bg-dark-800/50 rounded-lg p-6 border border-dark-700">
              <p className="text-gray-400 mb-4">
                If you have questions about this privacy policy, you can contact us.
              </p>
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                <span>© 2024 Aedon Community</span>
                <span>•</span>
                <span>Data Protection Compliant</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy; 